import React, { useState, useCallback, useMemo } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Search, Loader2, CheckCircle, X, Building2, MapPin,
  ChevronDown, ChevronUp, Sparkles, AlertTriangle, ChevronsDown,
  Hash, CreditCard
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
  'Content-Type': 'application/json',
});

// ─── India State Code → Canonical Name (Matches indianStates in forms) ─────────
const STATE_CODE_MAP = {
  "01": "Jammu and Kashmir",
  "02": "Himachal Pradesh",
  "03": "Punjab",
  "04": "Chandigarh",
  "05": "Uttarakhand",
  "06": "Haryana",
  "07": "Delhi",
  "08": "Rajasthan",
  "09": "Uttar Pradesh",
  "10": "Bihar",
  "11": "Sikkim",
  "12": "Arunachal Pradesh",
  "13": "Nagaland",
  "14": "Manipur",
  "15": "Mizoram",
  "16": "Tripura",
  "17": "Meghalaya",
  "18": "Assam",
  "19": "West Bengal",
  "20": "Jharkhand",
  "21": "Odisha",
  "22": "Chhattisgarh",
  "23": "Madhya Pradesh",
  "24": "Gujarat",
  "26": "Dadra and Nagar Haveli and Daman and Diu",
  "27": "Maharashtra",
  "29": "Karnataka",
  "30": "Goa",
  "31": "Lakshadweep",
  "32": "Kerala",
  "33": "Tamil Nadu",
  "34": "Puducherry",
  "35": "Andaman and Nicobar Islands",
  "36": "Telangana",
  "37": "Andhra Pradesh",
  "38": "Ladakh",
  "97": "Other Territory",
};

// ─── Resolve Canonical State Name (Guaranteed to match dropdowns) ─────────────
function canonicalState(val, fallbackGstin = '') {
  const g = (fallbackGstin || '').trim().toUpperCase();
  const codeState = g.length >= 2 ? STATE_CODE_MAP[g.substring(0, 2)] : '';

  if (!val || typeof val !== 'string') return codeState || '';
  const clean = val.trim();
  const lower = clean.toLowerCase();

  // If numeric code
  if (/^\d+$/.test(clean)) {
    return STATE_CODE_MAP[clean.padStart(2, '0')] || codeState || clean;
  }

  // Reject circle / ward / division names
  if (['ghatak', 'circle', 'ward', 'range', 'division'].some(k => lower.includes(k))) {
    return codeState || '';
  }

  // Case-insensitive match against standard Indian state names
  for (const name of Object.values(STATE_CODE_MAP)) {
    if (lower === name.toLowerCase()) return name;
    if (lower === name.toLowerCase().replace(' and ', ' & ')) return name;
  }

  return codeState || (clean.charAt(0).toUpperCase() + clean.slice(1));
}

// ─── Extract address from Attestr's primaryAddress / additionalAddresses ───────
function extractAttrestrAddr(addr, fallbackGstin = '') {
  if (!addr || typeof addr !== 'object') return {};

  const cleanGstin = (fallbackGstin || '').trim().toUpperCase();

  // 1. Street and building parts for address line 1
  const bldgParts = [addr.building, addr.buildingName, addr.floor, addr.street]
    .filter(Boolean)
    .map(s => String(s).trim())
    .filter(s => s && s.toLowerCase() !== 'null' && s.toLowerCase() !== 'undefined');

  const uniqueLine1 = [];
  bldgParts.forEach(part => {
    if (!uniqueLine1.some(existing => existing.toLowerCase() === part.toLowerCase())) {
      uniqueLine1.push(part);
    }
  });

  // 2. City: in India, city or district is the main urban / district name (Ahmedabad, Mumbai, etc.)
  const rawCity = (addr.city || addr.district || addr.locality || '').toString().trim();
  const cleanCity = rawCity && rawCity.toLowerCase() !== 'null'
    ? rawCity.charAt(0).toUpperCase() + rawCity.slice(1)
    : '';

  // 3. Address line 2: area, locality, or landmark (e.g. Matoda, PharmeZ, Dahej SEZ)
  const line2Candidates = [addr.locality, addr.area, addr.district]
    .filter(Boolean)
    .map(s => String(s).trim())
    .filter(s => s && s.toLowerCase() !== 'null' && s.toLowerCase() !== 'undefined');

  const line2Parts = [];
  line2Candidates.forEach(cand => {
    const candLower = cand.toLowerCase();
    if (
      candLower !== cleanCity.toLowerCase() &&
      !uniqueLine1.some(u => u.toLowerCase().includes(candLower)) &&
      !line2Parts.some(p => p.toLowerCase() === candLower)
    ) {
      line2Parts.push(cand);
    }
  });

  // 4. Canonical State (always matches indianStates dropdown)
  const resolvedState = canonicalState(addr.state || addr.stateCode || '', cleanGstin);

  // 5. Pincode
  const rawPin = (addr.zip || addr.pinCode || addr.pincode || addr.pncd || '').toString().trim();
  const pincode = rawPin.toLowerCase() !== 'null' ? rawPin : '';

  return {
    address_line_1: uniqueLine1.join(', ') || addr.fullAddress || '',
    address_line_2: line2Parts.join(', ') || '',
    city: cleanCity,
    state: resolvedState,
    pincode: pincode,
    country: addr.country || 'India',
  };
}

// ─── Build all branches from one record ───────────────────────────────────────
function buildBranches(record) {
  const gstin  = (record.gstin || '').trim().toUpperCase();
  const active = record.active !== false && String(record.status || '').toLowerCase().includes('active');

  const makeBranch = (addr, label, isHead) => {
    const a = extractAttrestrAddr(addr, gstin);
    const cleanState = a.state || canonicalState(record.state || '', gstin);
    const cleanCity  = a.city || '';
    const cleanArea  = (addr && addr.locality) ? String(addr.locality).trim() : '';

    let autoNick = label;
    if (isHead) {
      autoNick = cleanCity ? `HO – ${cleanCity}` : (cleanState ? `HO – ${cleanState}` : 'Head Office');
    } else {
      if (cleanArea && cleanArea.toLowerCase() !== cleanCity.toLowerCase()) {
        autoNick = `${cleanArea} Branch`;
      } else if (cleanCity) {
        autoNick = `${cleanCity} Branch`;
      } else if (cleanState) {
        autoNick = `${cleanState} Branch`;
      }
    }

    return {
      is_head_office:   isHead,
      branch_nick_name: autoNick,
      gst_number:       gstin,
      address_line_1:   a.address_line_1 || '',
      address_line_2:   a.address_line_2 || '',
      city:             cleanCity,
      state:            cleanState,
      pincode:          a.pincode || '',
      country:          a.country || 'India',
      contact_person:   '',
      designation:      '',
      contact_numbers:  [''],
      emails:           [''],
      is_active:        active,
    };
  };

  const branches = [];

  // Primary / head office address
  const primary = record.primaryAddress || record.principal || (record.addresses && record.addresses.find(x => x.type === 'PRIMARY')) || (record.addresses && record.addresses[0]) || null;
  branches.push(makeBranch(primary, 'Head Office', true));

  // Additional addresses (if any)
  const addl = record.additionalAddresses || record.additionalPlacesOfBusiness || (record.addresses && record.addresses.filter(x => x !== primary)) || [];
  (Array.isArray(addl) ? addl : []).forEach((addr, i) => {
    branches.push(makeBranch(addr, `Branch ${i + 1}`, false));
  });

  return branches;
}

// ─── Build branches from ALL records (one per GSTIN) ─────────────────────────
function buildBranchesFromAll(records) {
  return records.map((r, i) => {
    const gstin = (r.gstin || '').trim().toUpperCase();
    const a = extractAttrestrAddr(r.primaryAddress || (r.addresses && r.addresses[0]) || null, gstin);
    const stateName = a.state || canonicalState(r.state || '', gstin) || '';
    const cityName  = a.city || '';
    const autoLabel = i === 0
      ? (cityName ? `HO – ${cityName}` : (stateName ? `HO – ${stateName}` : 'Head Office'))
      : (cityName ? `${cityName} Branch` : (stateName ? `${stateName} Branch` : `Branch ${i + 1}`));
    return {
      is_head_office:   i === 0,
      branch_nick_name: autoLabel,
      gst_number:       gstin,
      address_line_1:   a.address_line_1 || '',
      address_line_2:   a.address_line_2 || '',
      city:             cityName,
      state:            stateName,
      pincode:          a.pincode || '',
      country:          a.country || 'India',
      contact_person:   '',
      designation:      '',
      contact_numbers:  [''],
      emails:           [''],
      is_active:        r.active !== false && String(r.status || '').toLowerCase().includes('active'),
    };
  });
}

// ─── PAN from GSTIN (chars 3–12) ─────────────────────────────────────────────
function panFromGstin(gstin) {
  if (gstin && gstin.length >= 12) return gstin.substring(2, 12).toUpperCase();
  return '';
}

// ─── Status badge ─────────────────────────────────────────────────────────────
function statusStyle(status, active) {
  if (active === false || (status || '').toLowerCase().includes('cancel'))
    return 'bg-red-100 text-red-700';
  if ((status || '').toLowerCase().includes('active'))
    return 'bg-green-100 text-green-700';
  return 'bg-yellow-100 text-yellow-700';
}

// ─── Single result card ───────────────────────────────────────────────────────
const GstResultCard = ({ record, onUse, idx }) => {
  const gstin   = (record.gstin || '').toUpperCase();
  const addr    = extractAttrestrAddr(record.primaryAddress || (record.addresses && record.addresses[0]) || null, gstin);
  const addrStr = [addr.address_line_1, addr.city, addr.state, addr.pincode].filter(Boolean).join(', ');
  const addlCount = (record.additionalAddresses || record.additionalPlacesOfBusiness || (record.addresses && record.addresses.slice(1)) || []).length;
  const [expanded, setExpanded] = useState(false);
  const [applying, setApplying] = useState(false);

  const handleApply = async () => {
    // If street or building is missing (e.g. from an unenriched PAN record), verify first
    const primary = record.primaryAddress || (record.addresses && record.addresses[0]);
    const needsEnrichment = (!primary || !primary.building || !primary.zip) && gstin;
    if (needsEnrichment) {
      setApplying(true);
      try {
        const res = await fetch(`${API}/api/gst/verify`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ gstin: gstin })
        });
        if (res.ok) {
          const data = await res.json();
          if (data && data.result) {
            onUse(data.result, buildBranches(data.result));
            setApplying(false);
            return;
          }
        }
      } catch (e) {
        console.error('Auto-enrichment error', e);
      }
      setApplying(false);
    }
    onUse(record, buildBranches(record));
  };

  return (
    <div
      data-testid={`gst-result-${idx}`}
      className="border border-gray-100 rounded-xl overflow-hidden hover:border-accent-blue/30 transition-colors bg-white shadow-xs"
    >
      <div className="flex items-start gap-3 p-4">
        <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
          <Building2 className="w-4 h-4 text-accent-blue" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div>
              <p className="font-semibold text-text-dark font-ibm text-sm">
                {record.legalName || record.tradeName || '(No name)'}
              </p>
              {record.tradeName && record.tradeName !== record.legalName && (
                <p className="text-xs text-text-secondary font-ibm">Trade: {record.tradeName}</p>
              )}
            </div>
            {(record.status || record.active !== undefined) && (
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full font-ibm ${statusStyle(record.status, record.active)}`}>
                {record.status || (record.active ? 'Active' : 'Inactive')}
              </span>
            )}
          </div>

          <div className="flex flex-wrap gap-2 mt-1.5 text-xs font-ibm">
            {gstin && (
              <span className="font-mono font-semibold text-accent-blue bg-blue-50 px-2 py-0.5 rounded border border-blue-100">
                {gstin}
              </span>
            )}
            {record.pan && (
              <span className="text-slate-600 font-mono bg-gray-100 px-1.5 py-0.5 rounded">PAN: {record.pan}</span>
            )}
            {(record.constitution || record.type) && (
              <span className="text-text-secondary">{record.constitution || record.type}</span>
            )}
            {record.registered && (
              <span className="text-text-secondary">Reg: {record.registered}</span>
            )}
            {record.einvoiceEnabled && (
              <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">e-Invoice</span>
            )}
          </div>

          {addrStr && (
            <div className="flex items-start gap-1 mt-1.5 text-xs text-text-secondary font-ibm">
              <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gray-400" />
              <span className="line-clamp-2">{addrStr}</span>
            </div>
          )}

          {addlCount > 0 && (
            <button
              onClick={() => setExpanded(v => !v)}
              className="mt-1 text-xs text-accent-blue font-ibm flex items-center gap-0.5 font-medium cursor-pointer"
            >
              {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {addlCount} additional address{addlCount > 1 ? 'es' : ''}
            </button>
          )}

          {expanded && (record.additionalAddresses || record.additionalPlacesOfBusiness || (record.addresses && record.addresses.slice(1)) || []).map((a, i) => {
            const ea = extractAttrestrAddr(a, gstin);
            const s = [ea.address_line_1, ea.city, ea.state, ea.pincode].filter(Boolean).join(', ');
            return (
              <div key={i} className="mt-1 text-xs text-text-secondary font-ibm pl-3 border-l-2 border-gray-100">
                <MapPin className="w-3 h-3 inline mr-1 text-gray-400" />{s || 'Address details not available'}
              </div>
            );
          })}
        </div>
      </div>

      {/* Action footer */}
      <div className="px-4 pb-3.5 pt-2 border-t border-gray-50 flex items-center justify-between gap-2 bg-gray-50/50">
        <Button
          data-testid={`gst-use-btn-${idx}`}
          size="sm"
          disabled={applying}
          onClick={handleApply}
          className="bg-accent-blue hover:bg-accent-blue/90 text-white font-ibm font-semibold h-8 px-4 rounded-lg text-xs cursor-pointer shadow-xs"
        >
          {applying ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5 mr-1.5" />}
          {applying ? 'Fetching Address...' : 'Use this & fill form'}
        </Button>
        {addlCount > 0 && (
          <span className="text-xs text-text-secondary font-ibm">
            {addlCount + 1} total address{addlCount > 0 ? 'es' : ''}
          </span>
        )}
      </div>
    </div>
  );
};

// ─── Main GstLookup component ─────────────────────────────────────────────────
const GstLookup = ({ initialName = '', onSelect, onClose }) => {
  const { toast } = useToast();
  const [query, setQuery]           = useState(initialName);
  const [searchMode, setSearchMode] = useState('auto');
  const [results, setResults]       = useState(null);
  const [loading, setLoading]       = useState(false);
  const [cached, setCached]         = useState(false);
  const [detectedType, setDetectedType] = useState(null);
  const [showAll, setShowAll]       = useState(false);

  const detectedPill = useMemo(() => {
    const clean = query.trim().replace(/\s+/g, '').toUpperCase();
    if (searchMode !== 'auto') {
      if (searchMode === 'gstin') return { label: 'GSTIN Verification API', color: 'bg-blue-50 text-blue-700 border-blue-200' };
      if (searchMode === 'pan') return { label: 'Search GSTIN by PAN API', color: 'bg-purple-50 text-purple-700 border-purple-200' };
      return { label: 'Search by Company Name API', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
    }
    if (clean.length === 15 && /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(clean)) {
      return { label: 'Detected: GSTIN (Direct Verification)', color: 'bg-blue-50 text-blue-700 border-blue-200' };
    }
    if (clean.length === 10 && /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(clean)) {
      return { label: 'Detected: PAN Number (Multi-state search)', color: 'bg-purple-50 text-purple-700 border-purple-200' };
    }
    if (clean.length >= 2) {
      return { label: 'Detected: Company Legal Name', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
    }
    return null;
  }, [query, searchMode]);

  const placeholderText = useMemo(() => {
    if (searchMode === 'gstin') return 'Enter 15-character GSTIN (e.g. 24ADXFS3875R1ZR)...';
    if (searchMode === 'pan') return 'Enter 10-character PAN (e.g. ADXFS3875R)...';
    if (searchMode === 'name') return 'Enter Company Legal Name (e.g. Stavan International)...';
    return 'Enter 15-digit GSTIN, 10-digit PAN, or Company Name...';
  }, [searchMode]);

  const handleSearch = useCallback(async () => {
    const q = query.trim();
    if (!q) return;
    setLoading(true);
    setResults(null);
    setShowAll(false);
    try {
      const res  = await fetch(`${API}/api/gst/search`, {
        method:  'POST',
        headers: authHeaders(),
        body:    JSON.stringify({ query: q, search_type: searchMode }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast({ title: 'Attestr GST Lookup Error', description: data.detail || 'Search failed', variant: 'destructive' });
        setLoading(false);
        return;
      }
      const list = Array.isArray(data.results) ? data.results : (data.result ? [data.result] : []);
      setResults(list);
      setCached(data.cached || false);
      setDetectedType(data.search_type || null);
    } catch (e) {
      toast({ title: 'Network error', description: 'Could not reach Attestr GST lookup service', variant: 'destructive' });
    }
    setLoading(false);
  }, [query, searchMode, toast]);

  const [importingAll, setImportingAll] = useState(false);

  const handleImportAll = async () => {
    if (!results || results.length === 0) return;
    setImportingAll(true);
    try {
      const enrichedRecords = await Promise.all(
        results.map(async (r) => {
          const g = (r.gstin || '').trim().toUpperCase();
          const p = r.primaryAddress || (r.addresses && r.addresses[0]);
          const missingAddr = !p || !p.building || !p.zip;
          if (missingAddr && g) {
            try {
              const res = await fetch(`${API}/api/gst/verify`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ gstin: g })
              });
              if (res.ok) {
                const data = await res.json();
                if (data && data.result) return data.result;
              }
            } catch (err) {
              console.warn(`Could not enrich GSTIN ${g}`, err);
            }
          }
          return r;
        })
      );

      const first = enrichedRecords[0];
      const branches = buildBranchesFromAll(enrichedRecords);
      onSelect(first, branches);
    } catch (err) {
      console.error('Import all error:', err);
    } finally {
      setImportingAll(false);
    }
  };

  const visible = showAll ? results : (results || []).slice(0, 5);
  const remaining = (results || []).length - 5;

  return (
    <div data-testid="gst-lookup-panel" className="bg-white border border-gray-200 rounded-xl shadow-xl p-5 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-accent-blue" />
          <span className="font-semibold text-text-dark font-ibm text-sm">Attestr Business Verification (GST / PAN / Name)</span>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 cursor-pointer">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Mode Selector Tabs */}
      <div className="flex items-center gap-1.5 p-1 bg-slate-100 rounded-lg text-xs font-ibm font-medium">
        <button
          type="button"
          onClick={() => setSearchMode('auto')}
          className={`flex-1 py-1.5 px-3 rounded-md transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
            searchMode === 'auto' ? 'bg-white text-accent-blue font-bold shadow-xs' : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <Sparkles className="w-3.5 h-3.5" /> Auto-Detect
        </button>
        <button
          type="button"
          onClick={() => setSearchMode('gstin')}
          className={`flex-1 py-1.5 px-3 rounded-md transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
            searchMode === 'gstin' ? 'bg-white text-accent-blue font-bold shadow-xs' : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <Hash className="w-3.5 h-3.5" /> GSTIN Verify
        </button>
        <button
          type="button"
          onClick={() => setSearchMode('pan')}
          className={`flex-1 py-1.5 px-3 rounded-md transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
            searchMode === 'pan' ? 'bg-white text-accent-blue font-bold shadow-xs' : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <CreditCard className="w-3.5 h-3.5" /> Search by PAN
        </button>
        <button
          type="button"
          onClick={() => setSearchMode('name')}
          className={`flex-1 py-1.5 px-3 rounded-md transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
            searchMode === 'name' ? 'bg-white text-accent-blue font-bold shadow-xs' : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <Building2 className="w-3.5 h-3.5" /> Company Name
        </button>
      </div>

      {/* Search bar & hint */}
      <div className="space-y-1.5">
        <div className="flex gap-2">
          <Input
            data-testid="gst-search-input"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            placeholder={placeholderText}
            className="border-gray-200 font-ibm focus:border-accent-blue flex-1 text-xs sm:text-sm h-10"
            autoFocus
          />
          <Button
            data-testid="gst-search-btn"
            onClick={handleSearch}
            disabled={loading || !query.trim()}
            className="bg-accent-blue hover:bg-accent-blue/90 text-white font-ibm font-semibold h-10 px-5 rounded-lg text-xs cursor-pointer"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            <span className="ml-1.5 hidden sm:inline">Search</span>
          </Button>
        </div>

        {detectedPill && (
          <div className="flex items-center gap-2">
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${detectedPill.color} font-ibm`}>
              {detectedPill.label}
            </span>
          </div>
        )}
      </div>

      {/* Cache / count info */}
      {results !== null && results.length > 0 && (
        <div className="flex items-center justify-between flex-wrap gap-2 pt-1 border-t border-gray-100">
          <p className="text-xs text-text-secondary font-ibm">
            {cached && <span className="text-green-600 mr-1"><CheckCircle className="w-3 h-3 inline mr-0.5" />Cached · </span>}
            <strong>{results.length}</strong> {detectedType === 'gstin' ? 'verified GSTIN record' : 'GSTIN registration'}{results.length > 1 ? 's' : ''} found
            {results.length > 1 && <span className="text-text-secondary/70"> (across different states)</span>}
          </p>
          {results.length > 1 && (
            <Button
              data-testid="gst-import-all-btn"
              size="sm"
              variant="outline"
              disabled={importingAll}
              onClick={handleImportAll}
              className="text-xs h-7 px-3 border-accent-blue/30 text-accent-blue font-ibm font-semibold cursor-pointer"
            >
              {importingAll ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ChevronsDown className="w-3.5 h-3.5 mr-1" />}
              {importingAll ? `Enriching & Importing ${results.length} Branches...` : `Import all ${results.length} as branches`}
            </Button>
          )}
        </div>
      )}

      {/* Empty state */}
      {results !== null && results.length === 0 && !loading && (
        <div className="flex flex-col items-center py-6 text-center">
          <AlertTriangle className="w-8 h-8 text-amber-300 mb-2" />
          <p className="text-gray-500 font-ibm text-sm font-medium">No records found</p>
          <p className="text-gray-400 font-ibm text-xs mt-1">Check the GSTIN, PAN, or name and try again</p>
        </div>
      )}

      {/* Result cards */}
      {results && results.length > 0 && (
        <div className="space-y-3 max-h-[460px] overflow-y-auto pr-1">
          {visible.map((r, i) => (
            <GstResultCard
              key={r.gstin || i}
              record={r}
              onUse={onSelect}
              idx={i}
            />
          ))}

          {!showAll && remaining > 0 && (
            <button
              onClick={() => setShowAll(true)}
              className="w-full py-2 text-xs text-accent-blue hover:text-accent-blue/80 font-ibm font-semibold flex items-center justify-center gap-1 border border-gray-100 rounded-xl cursor-pointer"
            >
              <ChevronDown className="w-3.5 h-3.5" />
              Show {remaining} more registration{remaining > 1 ? 's' : ''}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export { GstLookup, panFromGstin, buildBranchesFromAll, buildBranches };
export default GstLookup;
